"use client";

import { useState } from "react";
import appointmentQueryKeys from "@/app/(authenticated)/appointments/_modules/services/keys";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Calendar, Clock, MapPin, Video, Pencil, Trash2, Loader2 } from "lucide-react";
import { format } from "date-fns";
import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { NewAppointmentDialog } from "@/app/(authenticated)/appointments/_modules/components/NewAppointmentDialog";
import { getStatusDisplayName } from "@/app/(authenticated)/appointments/_modules/util/helpers";
import { statusColors } from "@/app/(authenticated)/appointments/_modules/constants/statusColors";
import { formatTimeWithTimezone } from "@/app/(authenticated)/appointments/_modules/util/timezone";
import { useUpdateAppointment, useDeleteAppointment } from "@/app/(authenticated)/appointments/_modules/hooks/queries";
import { ConfirmDeleteModal } from "@/app/(authenticated)/appointments/_modules/components/confirmDelete";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import type { TStatus } from "@/app/_module/types/filter-types";

interface LeadAppointmentsProps {
  leadId: string;
  leadName?: string; // Lead's name for appointment title auto-fill
  leadState?: string; // Lead's state for timezone display
  compact?: boolean; // For compact display in embedded view
  embedded?: boolean; // When true, hide outer Card wrapper
  /** Reports the loaded appointment count to the parent (History layout) */
}


interface Appointment {
  id: string;
  title: string;
  description?: string;
  notes?: string;
  apptNotes?: string;
  startTime: string;
  endTime: string;
  status: string;
  location?: string;
  meetingLink?: string;
  createdAt: string;
  updatedAt: string;
}

// Helper to check if appointment is in the past
const isPastAppointment = (startTime: string | undefined) => {
  if (!startTime) return false;
  return new Date(startTime) < new Date();
};

// Compact appointment item for embedded view
const CompactAppointmentItem: React.FC<{
  appointment: Appointment;
  onEdit: (apt: Appointment) => void;
  onStatusChange: (id: string, status: TStatus) => void;
  onDelete: (id: string) => void;
}> = ({ appointment, onEdit, onStatusChange, onDelete }) => {
  const statusOptions: TStatus[] = ['scheduled', 'rescheduled', 'cancelled', 'no_show'];
  const isPast = isPastAppointment(appointment.startTime);

  return (
    <div className={`flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded-lg border-b last:border-b-0 ${isPast ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`p-1.5 rounded-full ${isPast ? 'bg-gray-100' : 'bg-orange-100'}`}>
          <Calendar className={`h-3.5 w-3.5 ${isPast ? 'text-gray-400' : 'text-orange-600'}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium truncate ${isPast ? 'text-gray-500' : 'text-gray-900'}`}>
              {appointment.title || 'Appointment'}
            </p>
            {isPast && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 text-gray-400 border-gray-300">
                Past
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {appointment.startTime ? format(new Date(appointment.startTime), 'MMM d, h:mm a') : 'No date'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className={`text-xs ${statusColors[appointment.status || 'pending'] || statusColors.pending}`}
        >
          {getStatusDisplayName((appointment.status as TStatus) || 'pending')}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(appointment)}
          className="h-6 w-6 p-0"
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(appointment.id)}
          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export const LeadAppointments = ({ leadId, leadName, leadState, compact = false, embedded = false }: LeadAppointmentsProps) => {
  const [editingAppointment, setEditingAppointment] = useState<Appointment | undefined>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingAppointmentId, setDeletingAppointmentId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutate: updateAppointment } = useUpdateAppointment();
  const deleteAppointmentMutation = useDeleteAppointment();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: appointmentQueryKeys.byLead(leadId),
    queryFn: async () => {
      // Backend expects entityType=Lead and entityId=<leadId> for filtering appointments by lead
      const baseUrl = getApiUrl(endpoint.appointments.getAppointments);
      const params = new URLSearchParams({
        entityType: 'Lead',  // Match the case used when creating appointments
        entityId: leadId,
        page: '1',
        limit: '50'
      });
      const response = await api.get({
        url: `${baseUrl}?${params.toString()}`,
        auth: true,
      });

      // Backend returns: { statusCode, message, data: { meta }, items: [...] }
      // The appointments are in the top-level "items" array, NOT in "data"
      return (response as any)?.items || [];
    },
    staleTime: 5 * 1000, // Keep data fresh for 5 seconds to prevent race conditions
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  const handleEditClick = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAppointment(undefined);
  };

  const handleStatusChange = (appointmentId: string, newStatus: TStatus) => {
    // Optimistically update the local cache immediately
    queryClient.setQueryData(
      appointmentQueryKeys.byLead(leadId),
      (old: Appointment[] | undefined) => {
        if (!old) return old;
        return old.map((apt) =>
          apt.id === appointmentId
            ? { ...apt, status: newStatus, updatedAt: new Date().toISOString() }
            : apt
        );
      }
    );

    // Then send the update to the server
    updateAppointment({
      appointmentId,
      data: { status: newStatus },
    });
  };

  const handleDeleteClick = (appointmentId: string) => {
    setDeletingAppointmentId(appointmentId);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deletingAppointmentId) {
      deleteAppointmentMutation.mutate(deletingAppointmentId, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: appointmentQueryKeys.byLead(leadId) });
        },
      });
    }
    setConfirmDeleteOpen(false);
    setDeletingAppointmentId(null);
  };

  const statusOptions: TStatus[] = ['scheduled', 'rescheduled', 'cancelled', 'no_show'];
  const rawAppointments = (Array.isArray(data) ? data : []) as Appointment[];

  // Report the count upward once loaded (History layout compacts empty sections)
  // Sort appointments: upcoming first (closest date first), then past appointments (most recent first)
  const appointments = [...rawAppointments].sort((a, b) => {
    const now = new Date().getTime();
    const dateA = new Date(a.startTime).getTime();
    const dateB = new Date(b.startTime).getTime();
    const aIsUpcoming = dateA >= now;
    const bIsUpcoming = dateB >= now;

    // If both upcoming, closest first (ascending)
    if (aIsUpcoming && bIsUpcoming) {
      return dateA - dateB;
    }
    // If both past, most recent first (descending)
    if (!aIsUpcoming && !bIsUpcoming) {
      return dateB - dateA;
    }
    // Upcoming before past
    return aIsUpcoming ? -1 : 1;
  });

  // Compact view for embedded History tab
  if (compact) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 text-red-500 text-sm">
          Error loading appointments
        </div>
      );
    }

    if (appointments.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 text-sm">
          No appointments yet
        </div>
      );
    }

    return (
      <>
        {appointments.map((appointment) => (
          <CompactAppointmentItem
            key={appointment.id}
            appointment={appointment}
            onEdit={handleEditClick}
            onStatusChange={handleStatusChange}
            onDelete={handleDeleteClick}
          />
        ))}
        <NewAppointmentDialog
          open={isDialogOpen}
          onOpenChange={handleCloseDialog}
          editingAppointment={editingAppointment as any}
          preselectedLeadId={leadId}
          preselectedLeadName={leadName}
          preselectedLeadState={leadState}
        />
        <ConfirmDeleteModal
          message="Are you sure you want to delete this appointment? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onOpenChange={setConfirmDeleteOpen}
          open={confirmDeleteOpen}
          title="Delete Appointment"
        />
      </>
    );
  }

  // Full view (original implementation)
  if (isLoading) {
    const loadingContent = (
      <div className="text-center text-gray-500 py-6">Loading appointments...</div>
    );

    if (embedded) return loadingContent;

    return (
      <Card>
        <CardContent className="p-6">{loadingContent}</CardContent>
      </Card>
    );
  }

  if (error) {
    const errorContent = (
      <div className="text-center text-red-500 py-6">Error loading appointments</div>
    );

    if (embedded) return errorContent;

    return (
      <Card>
        <CardContent className="p-6">{errorContent}</CardContent>
      </Card>
    );
  }

  if (appointments.length === 0) {
    const emptyContent = (
      <div className="text-center text-gray-500 py-6">
        No appointments scheduled for this lead
      </div>
    );

    if (embedded) return emptyContent;

    return (
      <Card>
        <CardContent className="p-6">{emptyContent}</CardContent>
      </Card>
    );
  }

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'bg-gray-100 text-gray-800';

    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-primary/10 text-primary';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'rescheduled':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <div className="space-y-4">
        {appointments.map((appointment) => {
          const isPast = isPastAppointment(appointment.startTime);
          return (
          <Card key={appointment.id} className={isPast ? 'opacity-60 border-gray-200' : ''}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Calendar className={`h-4 w-4 ${isPast ? 'text-gray-400' : 'text-primary'}`} />
                    <span className={`font-medium ${isPast ? 'text-gray-500' : ''}`}>{appointment.title || 'Appointment'}</span>
                    {isPast && (
                      <Badge variant="outline" className="text-xs px-1.5 py-0 text-gray-400 border-gray-300">
                        Past
                      </Badge>
                    )}
                    <Select
                      value={appointment.status || 'pending'}
                      onValueChange={(value) => handleStatusChange(appointment.id, value as TStatus)}
                    >
                      <SelectTrigger className={`w-[140px] h-7 text-xs ${statusColors[appointment.status || 'pending'] || statusColors.pending}`}>
                        <SelectValue>
                          {getStatusDisplayName((appointment.status as TStatus) || 'pending')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((status) => (
                          <SelectItem key={status} value={status} className="text-xs">
                            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${statusColors[status]?.split(' ')[0]}`} />
                            {getStatusDisplayName(status)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {(() => {
                        const tzInfo = formatTimeWithTimezone(appointment.startTime, leadState);
                        if (!tzInfo) return <span>No date set</span>;
                        if (tzInfo.sameTimezone || !tzInfo.clientTime) {
                          return <span>{tzInfo.agentTime}</span>;
                        }
                        return (
                          <span className="flex items-center gap-2 flex-wrap">
                            <span>
                              <span className="font-medium">You:</span> {tzInfo.agentTime} <span className="text-xs text-gray-400">({tzInfo.agentTz})</span>
                            </span>
                            <span className="text-gray-300">|</span>
                            <span>
                              <span className="font-medium">Client:</span> {tzInfo.clientTime} <span className="text-xs text-gray-400">({tzInfo.clientTz})</span>
                            </span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {appointment.location && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{appointment.location}</span>
                    </div>
                  )}

                  {appointment.meetingLink && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Video className="h-4 w-4" />
                      <a
                        href={appointment.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Join Meeting
                      </a>
                    </div>
                  )}

                  {(appointment.description || appointment.notes || appointment.apptNotes) && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-xs font-medium text-gray-700 mb-1">Notes:</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {appointment.description || appointment.notes || appointment.apptNotes}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditClick(appointment)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(appointment.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      <NewAppointmentDialog
        open={isDialogOpen}
        onOpenChange={handleCloseDialog}
        editingAppointment={editingAppointment as any}
        preselectedLeadId={leadId}
        preselectedLeadName={leadName}
        preselectedLeadState={leadState}
      />
      <ConfirmDeleteModal
        message="Are you sure you want to delete this appointment? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onOpenChange={setConfirmDeleteOpen}
        open={confirmDeleteOpen}
        title="Delete Appointment"
      />
    </>
  );
};
